import { FC, useCallback } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import {
  SettingsIntegrationPageWrapper,
  SettingsIntegrationHeader,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
} from "@/pages/settings/components/settings-integration-layout";
import {
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSButton,
  MDSIcon,
  MDSIconKind,
  MDSIconColor,
  MDSTextColor,
  MDSListBox,
} from "@/mds";
import { useAppStore } from "@/store";
import { formatDateString } from "@/domains/date/date";
import { SessionObservable } from "@/store/sessions/SessionObservable";
import { DateTime } from "luxon";

function buildImportListItems(
  imports: SessionObservable[],
  handleView: (sessionId: string) => void
) {
  return imports.map(session => {
    const importDate = DateTime.fromISO(session.modelData.created_at);

    return {
      id: session.id,
      children: (
        <StyledImportItemContent>
          <MDSIcon kind={MDSIconKind.CheckCircleSolid} color={MDSIconColor.Positive} />
          <MDSText variant={MDSTextVariant.TextS}>
            {`Import on ${formatDateString(importDate, "LLLL d, yyyy")}`}
          </MDSText>
        </StyledImportItemContent>
      ),
      rightAddon: <MDSButton onClick={() => handleView(session.id)}>View</MDSButton>,
    };
  });
}

export const SettingsImportsModalPage: FC = observer(() => {
  const { store } = useAppStore();
  const completedImports = store.sessions.completedImportSessions;

  const handleViewImport = useCallback(
    async (sessionId: string) => {
      const batchId = await store.uploadedFileBatches.getBatchIdForSession(sessionId);
      if (!batchId) return;
      store.settingsModal.close();
      store.navigation.goToImportBatchNotes({ importBatchId: batchId, openInSameTab: false });
    },
    [store]
  );

  return (
    <SettingsIntegrationPageWrapper>
      <SettingsIntegrationHeader breadcrumb="Account" title="My imports" />
      <SettingsIntegrationScrollableArea>
        <SettingsIntegrationSection>
          <StyledSectionLabel variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
            COMPLETED IMPORTS
          </StyledSectionLabel>
          {completedImports.length === 0 ? (
            <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Tertiary}>
              Your import history will appear here.
            </MDSText>
          ) : (
            <MDSListBox items={buildImportListItems(completedImports, handleViewImport)} />
          )}
        </SettingsIntegrationSection>
      </SettingsIntegrationScrollableArea>
    </SettingsIntegrationPageWrapper>
  );
});

const StyledSectionLabel = styled(MDSText)`
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const StyledImportItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;
